/** @jsx jsx */
import React, { Fragment, useMemo, useRef } from 'react';
import { graphql, usePreloadedQuery, type PreloadedQuery } from 'react-relay';
import { jsx } from '@atlaskit/css';
import { JiraWorkspaceAri } from '@atlassian/ari/jira/workspace';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useActivationId } from '@atlassian/jira-tenant-context-controller/src/components/activation-id/index.tsx';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import { IdentityUserAri } from '@atlassian/ari/identity/user';
import { navPlatformSidebarResource } from '@atlassian/jira-nav-platform-integration-resources/src/services/sidebar/index.tsx';
import { useRelayResource } from '@atlassian/jira-relay-utils/src/services/resources/index.tsx';
import {
	getNav4SidebarId,
	useGetParamsForGetNav4SidebarId,
} from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebar-selector/src/common/logic.tsx';
import { SidebarNav4ContextProvider } from '@atlassian/jira-navigation-apps-sidebar-nav4-context/src/controllers/sidebar-context/index.tsx';
import type { SidebarId } from '@atlassian/jira-route-types/src/Navigation.tsx';
import type { GlobalSideNavigationSidebarsQuery } from './__generated__/GlobalSideNavigationSidebarsQuery.graphql';
import { GSNForOtherSidebars } from './GSNForOtherSidebars.tsx';
import { GSNForContentSidebar } from './GSNForContentSidebar.tsx';

type Props = {};

export function GlobalSideNavigationSidebars(_: Props): React.JSX.Element | null {
	const { queryReference } = useRelayResource<GlobalSideNavigationSidebarsQuery>(
		navPlatformSidebarResource,
	);
	const queryReferenceRef = useRef<PreloadedQuery<GlobalSideNavigationSidebarsQuery> | null>(null);
	const paramsForGetNav4SidebarId = useGetParamsForGetNav4SidebarId();
	const currentSidebarId = getNav4SidebarId(paramsForGetNav4SidebarId);

	// We don't want to use nullable query references. So only remember the non-null ones,
	// and pass the non-null query reference to the children.
	// From Slack: https://atlassian.slack.com/archives/C01E2MPPLE4/p1654736794416059?thread_ts=1654051102.548519&cid=C01E2MPPLE4
	if (queryReference) {
		// eslint-disable-next-line react-hooks/refs
		queryReferenceRef.current = queryReference;
		// eslint-disable-next-line react-hooks/refs
	} else if (!queryReferenceRef.current) {
		return null;
	}

	// We want this provider to be outside of GlobalSideNavigationSidebarsInner, so that we can use our
	// own SidebarNav4ContextProvider in storybooks. If it is inside, the storybook doesn't work when injecting
	//  the initial selected
	const Wrapper = currentSidebarId === 'CONTENT' ? SidebarNav4ContextProvider : Fragment;

	return (
		<Wrapper>
			<GlobalSideNavigationSidebarsInner
				// eslint-disable-next-line react-hooks/refs
				queryReference={queryReferenceRef.current}
				sidebarId={currentSidebarId}
			/>
		</Wrapper>
	);
}

type InnerProps = {
	isShortcutsStatic?: boolean;
	queryReference: PreloadedQuery<GlobalSideNavigationSidebarsQuery>;
	sidebarId: SidebarId;
};

export function GlobalSideNavigationSidebarsInner(props: InnerProps): React.JSX.Element {
	const { isShortcutsStatic = false, queryReference, sidebarId } = props;
	const queryRef = usePreloadedQuery<GlobalSideNavigationSidebarsQuery>(
		// Need to disable this rule for the useNetworkSideNavUserData fragment (which is inside GSN)
		/* eslint-disable @atlassian/relay/must-colocate-fragment-spreads */
		graphql`
			query GlobalSideNavigationSidebarsQuery(
				$cloudId: ID!
				$entityAriPlans: ID!
				$entityAriProjects: ID!
				$entityAriUserSideNavSettings: ID!
				$isAppsExpanded: Boolean = false
				$isFiltersExpanded: Boolean = false
				$isPlansExpanded: Boolean = false
				$isProjectsExpanded: Boolean = false
				$isOperationsExpanded: Boolean = false
				$isOverviewsExpanded: Boolean = false
				$navPlatSidebarDataEntityAri: ID!
				$navPlatMenuDataEntityAri: ID!
				$ownerAri: ID
			) {
				...useNetworkSideNavUserData
				navPlatL1MenuData: settings_navigationCustomisation(
					entityAri: $navPlatMenuDataEntityAri
					ownerAri: $ownerAri
				) {
					...useNavSettingsService
				}
				navPlatSidebarData: settings_navigationCustomisation(
					entityAri: $navPlatSidebarDataEntityAri
					ownerAri: $ownerAri
				) {
					...useNavSettingsService
				}
				...GSNForContentSidebar
			}
		`,
		queryReference,
	);
	const cloudId = useCloudId();
	const activationId = useActivationId();
	const ownerId = useAccountId();

	// Note: When Jira GSN adoption rolls out, it should rollout with "network-migration" for 3 months
	//  to give time for all old user settings to be stored in the new records.
	//
	//    - customizationStore="network-migration"
	//
	//  After 3 months of being rolled-out, create a new FG to:
	//   - Change customizationStore to "network"
	//   - @include(if: $fgVariable) for useNetworkSideNavUserData.
	//   - After that has rolled out,
	//
	const customizationStore = 'network-migration';
	const networkStorageParams = useMemo(() => {
		const siteId = cloudId;
		const entityAri = JiraWorkspaceAri.create({ siteId, activationId }).toString();
		const ownerAri = (ownerId && IdentityUserAri.create({ userId: ownerId }).toString()) || '';

		return { activationId, entityAri, ownerAri, siteId, queryRef };
	}, [cloudId, activationId, ownerId, queryRef]);

	if (sidebarId === 'CONTENT') {
		return (
			<GSNForContentSidebar
				customizationStore={customizationStore}
				networkStorageParams={networkStorageParams}
				queryReference={queryRef}
				isShortcutsStatic={isShortcutsStatic}
			/>
		);
	}
	return (
		<GSNForOtherSidebars
			sidebarId={sidebarId}
			customizationStore={customizationStore}
			networkStorageParams={networkStorageParams}
		/>
	);
}
